

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  loadLocalTemplates,
  templates,
  templatesLoaded,
} from '@/stores/websiteTemplatesStore'
import {
  builderContent,
  content,
  companyWebsite,
  saveWebsite,
  selectWebsiteTemplate,
  replaceBuilderContent,
  replaceContent,
  syncPreviewDraft,
  websiteBuilderState,
} from '@/stores/websiteBuilderStore'
import { company, website, isAdmin, getAuthToken, API_BASE_URL, isDevUser, selectedSetting } from '@/stores/appConfig.js'
import ImagePickerModal from '@/components/builder/ImagePickerModal.vue'
import FieldRenderer from '@/components/builder/FieldRenderer.vue'
import { FONTS } from '@/config/fonts' //import fonts


const route = useRoute()
const router = useRouter()

// Sentinel template_id BuilderView3.vue uses for AI-generated/pasted raw-HTML
// sites (see src/views/SiteView.vue) — reserved so no real template can ever
// collide with it (backend/routes/templates.js). When a company's live site
// carries this id, it was built with the other system entirely: the JSON-
// block template lookup below already correctly no-ops rather than trying
// to misinterpret raw HTML as block content, but it did so silently — this
// makes that explicit instead of just showing an empty/default builder.
const RAW_HTML_TEMPLATE_ID = 'raw-html'
const builtWithOtherSystem = computed(() =>
  !adminCompanyId.value && website.template_id === RAW_HTML_TEMPLATE_ID
)

// ── Admin mode ────────────────────────────────────────────────────────────
const adminCompanyId = computed(() =>
  isAdmin.value && route.query.company_id ? route.query.company_id : null
)
const adminWebsite = ref(null)
const adminLoading = ref(false)
const siteTitle = ref('')
const metaDescription = ref('')

onMounted(async () => {
  if (adminCompanyId.value) {
    adminLoading.value = true
    try {
      const token = await getAuthToken()
      const res = await fetch(`${API_BASE_URL}/admin/websites/${adminCompanyId.value}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        adminWebsite.value = data.website
        siteTitle.value = data.website?.site_title || ''
        metaDescription.value = data.website?.meta_description || ''
      }
    } catch (err) {
      console.error('Failed to load admin website', err)
    } finally {
      adminLoading.value = false
    }
  }
})

// Initialise admin-mode builder once BOTH the website record and templates are ready.
watch(
  [templatesLoaded, () => adminWebsite.value],
  ([loaded, aw]) => {
    if (!loaded || !aw || !adminCompanyId.value) return

    if (aw.template_id) {
      websiteBuilderState.selectedTemplateId = aw.template_id
      companyWebsite.templateSelected = aw.template_id

      const template = templates.find(t => t.id === aw.template_id)
      if (template) replaceBuilderContent(template.content)
    }

    const savedContent = aw.content
      ? typeof aw.content === 'string' ? JSON.parse(aw.content) : aw.content
      : null
    if (savedContent) replaceContent(savedContent)
  },
  { immediate: true }
)
/*
const effectiveQuotasticDomain = computed(() => 
  adminCompanyId.value
    ? adminWebsite.value?.quotastic_domain
    : website.quotastic_domain

)
*/

const effectiveQuotasticDomain = computed(() => {
  
  if(adminCompanyId.value){
    return adminWebsite.value?.quotastic_domain
  }
  else if (website.quotastic_domain){
    return website.quotastic_domain
  }
  else{
    return company.company_id
  }
 

})
 
 
const effectiveCompanyLabel = computed(() => {
  if (!adminCompanyId.value) return null
  return adminWebsite.value?.quotastic_domain || adminCompanyId.value
})

watch(
  () => website.site_title,
  (title) => {
    if (!adminCompanyId.value) {
      siteTitle.value = title || ''
    }
  },
  { immediate: true }
)

watch(
  () => website.meta_description,
  (description) => {
    if (!adminCompanyId.value) {
      metaDescription.value = description || ''
    }
  },
  { immediate: true }
)



/*--------------------------------------------------
| Helper Functions
--------------------------------------------------*/

// Template Image Gallery
function addGalleryImage() {
  content.gallery.images.value.push({
    url: '',
    caption: '',
    size: ''
  })
}

function removeGalleryImage(index) {
  content.gallery.images.value.splice(index, 1)
}

function moveGalleryImage(index, direction) {
  const images = content.gallery?.images?.value
  const nextIndex = index + direction

  if (!images || nextIndex < 0 || nextIndex >= images.length) {
    return
  }

  const [image] = images.splice(index, 1)
  images.splice(nextIndex, 0, image)
}

function sectionFields(section) {
  if (!section) return {}
  const { _meta, ...fields } = section
  return Object.fromEntries(
    Object.entries(fields).filter(([, f]) => f?.type !== 'hidden')
  )
}
/*--------------------------------------------------
| End Helper Functions
--------------------------------------------------*/



const sectionsGlobal = [
  { icon: 'mdi-view-dashboard-outline', label: 'Template' },
  { icon: 'mdi-clock-outline', label: 'Versions' },
  //{ icon: 'mdi-connection', label: 'Integrations' },
  { icon: 'mdi-magnify', label: 'SEO' },
 
  { icon: 'mdi-code-tags', label: 'Custom Code' },
]

const sectionSelected = ref('Template')
const activeTemplate = computed(() =>
  templates.find(template => template.id === websiteBuilderState.selectedTemplateId) || null
)
// A contractor can still have a template here from before the AI-proposal
// feature (below) was disabled — loadLocalTemplates() (see onMounted)
// restores anything already staged in this browser's storage. This flag
// gates Save/Publish against that stale, DB-less content; it's not dead
// code just because nothing can newly create a local template anymore.
const isLocalTemplateActive = computed(() => Boolean(activeTemplate.value?.local))

function sectionSelect(section){

  sectionSelected.value = section
  //alert(sectionSelected.value)

}



 
function cloneForPreview(value) {
  return JSON.parse(JSON.stringify(value))
}

function currentPreviewWebsite() {
  return {
    id: adminWebsite.value?.id || website.id || null,
    company_id: adminCompanyId.value || company.company_id,
    domain: adminWebsite.value?.domain || website.domain || null,
    quotastic_domain:
      adminWebsite.value?.quotastic_domain ||
      website.quotastic_domain ||
      company.company_id ||
      null,
    site_title: siteTitle.value || null,
    meta_description: metaDescription.value || null,
    template_id: websiteBuilderState.selectedTemplateId,
  }
}

function syncLivePreview() {
  const previewWebsite = currentPreviewWebsite()
  const previewContent = cloneForPreview(content)
  const localTemplate = activeTemplate.value?.local
    ? cloneForPreview(activeTemplate.value)
    : null

  syncPreviewDraft({
    website: previewWebsite,
    siteTitle: siteTitle.value || '',
    metaDescription: metaDescription.value || '',
    templateId: websiteBuilderState.selectedTemplateId,
    nextContent: previewContent
  })

  const previewFrame = document.getElementById('previewFrame')
  previewFrame?.contentWindow?.postMessage(
    {
      type: 'quotastic:builder-preview',
      website: previewWebsite,
      content: previewContent,
      template_id: websiteBuilderState.selectedTemplateId,
      site_title: siteTitle.value || '',
      meta_description: metaDescription.value || '',
      inline_template: localTemplate,
    },
    window.location.origin
  )
}

let previewHasSynced = false

watch(
  [
    content,
    () => websiteBuilderState.selectedTemplateId,
    siteTitle,
    metaDescription,
    effectiveQuotasticDomain
  ],
  () => {
    if (previewHasSynced) {
      websiteBuilderState.dirty = true
    }

    syncLivePreview()
    previewHasSynced = true
  },
  { deep: true, immediate: true }
)

async function saveSite() {
  if (isLocalTemplateActive.value) {
    publishError.value =
      'This template is stored locally. Promote it to the shared template database before saving the site.'
    return
  }

  if (adminCompanyId.value) {
    try {
      const token = await getAuthToken()
      const response = await fetch(`${API_BASE_URL}/sites/draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id:       adminCompanyId.value,
          quotastic_domain: adminWebsite.value?.quotastic_domain,
          site_title:       siteTitle.value || null,
          meta_description: metaDescription.value || null,
          template_id:      websiteBuilderState.selectedTemplateId,
          content,
        }),
      })
      const data = await response.json().catch(() => ({}))

      if (!response.ok || data.ok === false) {
        throw new Error(data.error || `Save failed: ${response.status}`)
      }

      websiteBuilderState.dirty = false
    } catch (err) {
      console.error('Admin save failed', err)
    }
    return
  }
  await saveWebsite({
    site_title: siteTitle.value || null,
    meta_description: metaDescription.value || null,
  })
  websiteBuilderState.dirty = false
}
const isPublishing = ref(false)
const publishError = ref('')
const latestDeploymentUrl = ref('')

function defaultPublishProjectName() {
  return (
    //effectiveQuotasticDomain.value ||
    company.company_id 
    //||'quotastic-site'
  )
}

function publishWebsitePayload(projectName) {
  return {
    id: adminWebsite.value?.id || website.id || projectName,
    company_id: adminCompanyId.value || company.company_id,
    domain: adminWebsite.value?.domain || website.domain || null,
    quotastic_domain:
      adminWebsite.value?.quotastic_domain ||
      website.quotastic_domain ||
      projectName,
    site_title: siteTitle.value || null,
    meta_description: metaDescription.value || null,
    template_id: websiteBuilderState.selectedTemplateId,
  }
}


async function publishSite({
  projectName = defaultPublishProjectName(),
  branch = 'main'
} = {}) {
  if (isLocalTemplateActive.value) {
    publishError.value =
      'Local AI templates cannot be published until they are promoted to the template database.'
    return null
  }

  isPublishing.value = true
  publishError.value = ''
  latestDeploymentUrl.value = ''

  try {
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/sites/publish`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        branch,
        website: publishWebsitePayload(projectName),
        content,
        template_id: websiteBuilderState.selectedTemplateId,
      }),
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok || data.ok === false) {
      throw new Error(data.error || `Publish failed: ${response.status}`)
    }

    latestDeploymentUrl.value =
      data.deployment_url ||
      (data.published_version?.version_number
        ? `Published version ${data.published_version.version_number}`
        : data.message || 'Site published.')
    console.log('Publish result:', data)

    return data
  } catch (error) {
    publishError.value = error.message || 'Publish failed.'
    console.error('Publish failed:', error)
    throw error
  } finally {
    isPublishing.value = false
  }
}

// ── Render to static HTML (shared by preview + Cloudflare deploy) ───────
// Builder1's content is JSON blocks compiled through a Vue template at
// render time, not the plain HTML Cloudflare Pages / the preview-link
// system expect. /sites/render (websitePublisher.js, previously built but
// never wired to any UI) compiles it server-side via @vue/server-renderer
// into a single self-contained HTML string — same shape as Builder3's raw
// HTML — so both features below can reuse Builder3's existing, proven
// /sites/deploy-pages and preview routes unchanged instead of needing
// JSON-block-aware versions of them.
async function renderCurrentSiteToHtml() {
  const response = await fetch(`${API_BASE_URL}/sites/render`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      company_id: adminCompanyId.value || company.company_id,
      template_id: websiteBuilderState.selectedTemplateId,
      content,
      site_title: siteTitle.value || null,
    }),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok || data.ok === false) {
    throw new Error(data.error || `Unable to render this site (${response.status}).`)
  }
  return data.html
}

// ── Preview link ──────────────────────────────────────────────────────────
// Replaces the old "Preview site" link (/site-preview/:id), which relied on
// live in-memory state (postMessage / the previewDraft store) that doesn't
// exist in a freshly opened tab, and fell back to fetching /websites_draft —
// a route that doesn't exist on the backend at all. This instead renders the
// current content to real HTML and hands it to the exact same shareable,
// versioned preview-link system Builder3 already uses (see
// /sites/preview-render — a variant of Builder3's /sites/preview that never
// touches this site's actual draft row, since this render is a one-off
// snapshot, not something to save over the live draft).
const previewing = ref(false)
const previewError = ref('')
const previewUrl = ref('')

async function getPreviewLink() {
  previewing.value = true
  previewError.value = ''
  previewUrl.value = ''

  try {
    const html = await renderCurrentSiteToHtml()
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/sites/preview-render`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        company_id: adminCompanyId.value || company.company_id,
        html,
        title: siteTitle.value || null,
      }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) {
      throw new Error(data.error || `Unable to create a preview link (${response.status}).`)
    }

    const base = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:8080'
      : 'https://quotastic.ca'
    previewUrl.value = `${base}/preview/${data.preview_token}`
  } catch (error) {
    previewError.value = error.message || 'Unable to create a preview link.'
  } finally {
    previewing.value = false
  }
}

// ── Publish to Cloudflare ────────────────────────────────────────────────
// Distinct from publishSite() above, which despite its name/icon only ever
// wrote to the SQL-backed draft/published version (website_versions) — it
// never deployed anything to Cloudflare Pages, which is what actually makes
// a custom domain resolve to this site (see backend/routes/pagesDeploy.js
// and the domain-reservation flow in DomainView.vue). That gap meant a
// contractor using this builder plus a custom domain would have nothing
// real deploying their Builder1 content to the Pages project their domain
// points at. Reuses the same /sites/deploy-pages route Builder3 calls,
// unchanged — it only ever needed a plain HTML string, and now has one.
const publishingToCloudflare = ref(false)
const cloudflarePublishError = ref('')
const cloudflareDeploymentUrl = ref('')

async function publishToCloudflare() {
  if (isLocalTemplateActive.value) {
    cloudflarePublishError.value =
      'This template is stored locally. Promote it to the shared template database before publishing.'
    return
  }

  publishingToCloudflare.value = true
  cloudflarePublishError.value = ''
  cloudflareDeploymentUrl.value = ''

  try {
    const html = await renderCurrentSiteToHtml()
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/sites/deploy-pages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        company_id: adminCompanyId.value || company.company_id,
        html,
        title: siteTitle.value || null,
      }),
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.ok) {
      throw new Error(data.error || `Cloudflare Pages deploy failed (${response.status}).`)
    }

    cloudflareDeploymentUrl.value = data.deployment_url || ''
  } catch (error) {
    cloudflarePublishError.value = error.message || 'Unable to publish to Cloudflare Pages.'
  } finally {
    publishingToCloudflare.value = false
  }
}

function pickTemplate(id){
  selectWebsiteTemplate(id)
}


// When Template ID is loaded, replace builderContent object
//const contentObj = ref('')
watch(
  [() => website.template_id, templatesLoaded],
  ([templateId]) => {
    if (adminCompanyId.value || !templateId || !templatesLoaded.value) return

    const template = templates.find(t => t.id === templateId)

    if (!template) {
      console.warn(`Template not found: ${templateId}`)
      return
    }

    websiteBuilderState.selectedTemplateId = templateId
    companyWebsite.templateSelected = templateId
    replaceBuilderContent(template.content)

    if (website.content) {
      replaceContent(
        typeof website.content === 'string'
          ? JSON.parse(website.content)
          : website.content
      )
    }
  },
  { immediate: true }
)

const visibleSections = computed(() =>
  Object.entries(builderContent).map(([key, section]) => ({
    key,
    label: section._meta?.label ?? key,
    icon: section._meta?.icon ?? 'mdi-cog'
  }))
)

const activeSectionKey = computed(() =>
  visibleSections.value.find(s => s.label === sectionSelected.value)?.key ?? null
)

 
//───────────────────────────────────────────────────────────
// Image Modal
//───────────────────────────────────────────────────────────
const imagePickerOpen = ref(false)
const imagePickerTargetIndex = ref(null)
const imagePickerFieldTarget = ref(null)   // { sectionKey, fieldKey } when opened from a FieldRenderer image field
const imagePickerSelectedUrl = ref('')
const tenantImages = ref([])
const tenantImagesLoading = ref(false)
const tenantImagesUploading = ref(false)
const tenantImagesUploadError = ref('')
const tenantImageDeleting = ref(null)

const effectiveTenantId = computed(() =>
  adminCompanyId.value || company.company_id
)

const loadedLocalTemplateCompanies = new Set()
watch(
  effectiveTenantId,
  async companyId => {
    if (!companyId || loadedLocalTemplateCompanies.has(companyId)) return

    loadedLocalTemplateCompanies.add(companyId)
    try {
      await loadLocalTemplates(companyId)
    } catch (error) {
      console.warn('[templates] Unable to load local AI templates:', error.message)
    }
  },
  { immediate: true }
)

async function loadTenantImages() {
  if (!effectiveTenantId.value) return

  tenantImagesLoading.value = true
  tenantImagesUploadError.value = ''

  try {
    const token = await getAuthToken()

    // Swap this endpoint for your real tenant image endpoint.
    const response = await fetch(
      `${API_BASE_URL}/media?company_id=${effectiveTenantId.value}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      }
    )

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      throw new Error(data.error || 'Unable to load images.')
    }

    tenantImages.value = data.images || data.media || []
  }
  catch (error) {
    console.error('loadTenantImages error:', error)
    tenantImagesUploadError.value = error.message || 'Unable to load images.'
  }
  finally {
    tenantImagesLoading.value = false
  }
}

function openGalleryImagePicker(index) {
  imagePickerTargetIndex.value = index
  imagePickerFieldTarget.value = null
  imagePickerSelectedUrl.value = content.gallery?.images?.value?.[index]?.url || ''
  tenantImagesUploadError.value = ''
  imagePickerOpen.value = true
  loadTenantImages()
}

function handleImagePickFromField({ sectionKey, fieldKey }) {
  imagePickerFieldTarget.value = { sectionKey, fieldKey }
  imagePickerTargetIndex.value = null
  imagePickerSelectedUrl.value = content[sectionKey]?.[fieldKey]?.value || ''
  tenantImagesUploadError.value = ''
  imagePickerOpen.value = true
  loadTenantImages()
}

function handleImageSelect(image) {
  if (!image?.url) return

  if (imagePickerFieldTarget.value) {
    const { sectionKey, fieldKey } = imagePickerFieldTarget.value
    if (!content[sectionKey]) content[sectionKey] = {}
    if (!content[sectionKey][fieldKey]) {
      content[sectionKey][fieldKey] = { ...(builderContent[sectionKey]?.[fieldKey] ?? { type: 'image', label: '', value: '' }) }
    }
    content[sectionKey][fieldKey].value = image.url
  } else {
    selectGalleryImage(image)
  }
}

function selectGalleryImage(image) {
  const galleryImage =
    content.gallery?.images?.value?.[imagePickerTargetIndex.value]

  if (!galleryImage || !image?.url) return

  galleryImage.url = image.url

  if (!galleryImage.caption && (image.caption || image.name || image.filename)) {
    galleryImage.caption = image.caption || image.name || image.filename
  }
}

function addTenantImage(image) {
  if (!image?.url) return

  const existingIndex = tenantImages.value.findIndex(existing =>
    existing.cloudflare_image_id
      ? existing.cloudflare_image_id === image.cloudflare_image_id
      : existing.url === image.url
  )

  if (existingIndex >= 0) {
    tenantImages.value.splice(existingIndex, 1, image)
    return
  }

  tenantImages.value.unshift(image)
}

function cloudflareErrorMessage(data, fallback) {
  return (
    data?.errors?.map(error => error.message).join('; ') ||
    data?.error ||
    fallback
  )
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function completeTenantImageUpload({ token, tenantId, cloudflareImageId, filename }) {
  let lastError = null

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const response = await fetch(`${API_BASE_URL}/media/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        company_id: tenantId || 'admin',
        cloudflare_image_id: cloudflareImageId,
        filename,
      }),
    })
    const data = await response.json().catch(() => ({}))

    if (response.ok && data.image?.url) {
      return data.image
    }

    lastError = new Error(data.error || 'Unable to save uploaded image.')

    if (response.status !== 409 || attempt === 3) {
      throw lastError
    }

    await wait(700)
  }

  throw lastError || new Error('Unable to save uploaded image.')
}

async function uploadTenantImage(file) {
  const tenantId = effectiveTenantId.value

  if (!tenantId || !file) {
    tenantImagesUploadError.value = 'Company is still loading. Try again in a moment.'
    return
  }

  tenantImagesUploading.value = true
  tenantImagesUploadError.value = ''

  try {
    const token = await getAuthToken()

    if (!token) {
      throw new Error('You need to be signed in to upload images.')
    }

    const directResponse = await fetch(`${API_BASE_URL}/media/direct-upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        company_id: tenantId,
        filename: file.name,
      }),
    })
    const directData = await directResponse.json().catch(() => ({}))
    const cloudflareImageId = directData.image?.cloudflare_image_id

    if (!directResponse.ok || !directData.uploadURL || !cloudflareImageId) {
      throw new Error(
        directData.error || 'Unable to prepare image upload.'
      )
    }

    const uploadForm = new FormData()
    uploadForm.append('file', file)

    const uploadResponse = await fetch(directData.uploadURL, {
      method: 'POST',
      body: uploadForm,
    })
    const uploadData = await uploadResponse.json().catch(() => ({}))

    if (!uploadResponse.ok || uploadData.success === false) {
      throw new Error(
        cloudflareErrorMessage(uploadData, 'Cloudflare upload failed.')
      )
    }

    const uploadedImage = await completeTenantImageUpload({
      token,
      tenantId,
      cloudflareImageId,
      filename: file.name,
    })

    addTenantImage(uploadedImage)
    handleImageSelect(uploadedImage)
    imagePickerOpen.value = false
  }
  catch (error) {
    console.error('uploadTenantImage error:', error)
    tenantImagesUploadError.value = error.message || 'Unable to upload image.'
  }
  finally {
    tenantImagesUploading.value = false
  }
}
async function deleteTenantImage(image) {
  const mediaId = image?.id
  if (!mediaId) return
  tenantImageDeleting.value = mediaId
  try {
    const token = await getAuthToken()
    const res = await fetch(`${API_BASE_URL}/media/${mediaId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error || 'Delete failed.')
    }
    tenantImages.value = tenantImages.value.filter(img => img.id !== mediaId)
  } catch (error) {
    console.error('deleteTenantImage error:', error)
    tenantImagesUploadError.value = error.message || 'Unable to delete image.'
  } finally {
    tenantImageDeleting.value = null
  }
}
//───────────────────────────────────────────────────────────
// End Image Modal
//───────────────────────────────────────────────────────────




//───────────────────────────────────────────────────────────
// Font Selector
//───────────────────────────────────────────────────────────

function updateFontImport() {
  const headingFont = content.theme?.heading_font?.value
  const bodyFont = content.theme?.body_font?.value

  const imports = [headingFont, bodyFont]
    .filter(Boolean)
    .map(name => FONTS.find(f => f.value === name)?.importUrl)
    .filter(Boolean)
    .filter((url, i, arr) => arr.indexOf(url) === i) // dedupe if same font picked for both
    .map(url => `@import url('${url}');`)
    .join('\n')

  if (content.theme?.font_import) {
    content.theme.font_import.value = imports
  }
}

//───────────────────────────────────────────────────────────
// End Font Selector
//───────────────────────────────────────────────────────────

console.log('builderview isDevUser = ',isDevUser.value)
</script>













<template>


<div class="mobile-builder-message">
  <v-icon>mdi-monitor</v-icon>
  <h2>Builder is best on desktop</h2>
  <p>Open this page on a larger screen to edit your site.</p>
</div>


<div class="desktop-builder">
  <!-- Builder UI -->
  <div class="builder-page">



    <!--──────────────────────────────────────────────────────────
    | Left Panel
    ───────────────────────────────────────────────────────────-->
    <aside class="builder-panel builder-panel-left">
  
      <button
        v-for="section in sectionsGlobal"
        :key="section.label"
        class="tool-button"
        type="button"
        @click="sectionSelect(section.label)"
          :class="{ active: sectionSelected === section.label }"
      >
        <v-icon size="22">{{ section.icon }}</v-icon>
        <span>{{ section.label }}</span>
      </button>

      <v-divider class="my-4"></v-divider>

      <button
        v-for="section in visibleSections"
        :key="section.key"
        class="tool-button"
        type="button"
        @click="sectionSelect(section.label)"
        :class="{ active: sectionSelected === section.label }"
      >
        <v-icon size="22">{{ section.icon }}</v-icon>
        <span>{{ section.label }}</span>
      </button>
    </aside>




    <!--───────────────────────────────────────────────────────────
    | Preview Panel
    ───────────────────────────────────────────────────────────-->
    <main class="preview-area">
      <div v-if="effectiveCompanyLabel" class="admin-banner">
        <v-icon size="16">mdi-shield-account-outline</v-icon>
        Editing site for: <strong>{{ effectiveCompanyLabel }}</strong>
        <button class="admin-banner-back" @click="router.push('/admin')">
          <v-icon size="15">mdi-arrow-left</v-icon> Back to Admin
        </button>
      </div>

      <div class="preview-toolbar">
        <div>
          <strong>Published Site:</strong>
          <span>https://quotastic.ca/site/{{ effectiveQuotasticDomain }}</span>
          <span
            v-if="publishError || latestDeploymentUrl"
            class="publish-status"
            :class="{ 'publish-status-error': publishError }"
          >
            {{ publishError || latestDeploymentUrl }}
          </span>
          <span
            v-if="cloudflarePublishError || cloudflareDeploymentUrl"
            class="publish-status"
            :class="{ 'publish-status-error': cloudflarePublishError }"
          >
            {{ cloudflarePublishError || `Live on Cloudflare Pages: ${cloudflareDeploymentUrl}` }}
          </span>
          <span
            v-if="previewError || previewUrl"
            class="publish-status"
            :class="{ 'publish-status-error': previewError }"
          >
            <template v-if="previewUrl">
              Preview link: <a :href="previewUrl" target="_blank" rel="noopener">{{ previewUrl }}</a>
            </template>
            <template v-else>{{ previewError }}</template>
          </span>
        </div>
        <div class="preview-toolbar-actions">
          <button
            class="btn-ghost"
            type="button"
            :disabled="isLocalTemplateActive"
            :title="isLocalTemplateActive ? 'Promote this local template before saving the site' : 'Save site'"
            @click="saveSite"
          >
            <v-icon size="18">mdi-content-save-outline</v-icon>
            Save
          </button>

          <button
            class="btn-ghost"
            type="button"
            :disabled="isPublishing || isLocalTemplateActive"
            :title="isLocalTemplateActive ? 'Promote this local template before publishing' : 'Mark this draft as the officially published version (SQL version history — not a live Cloudflare deployment)'"
            @click="publishSite()"
          >
            <v-icon size="18">mdi-check-decagram-outline</v-icon>
            {{ isPublishing ? 'Publishing...' : 'Publish version' }}
          </button>

          <button
            class="btn-primary"
            type="button"
            :disabled="publishingToCloudflare || isLocalTemplateActive"
            :title="isLocalTemplateActive ? 'Promote this local template before publishing' : 'Deploy this site to Cloudflare Pages — makes it live on your custom domain'"
            @click="publishToCloudflare"
          >
            <v-icon size="18">mdi-cloud-upload-outline</v-icon>
            {{ publishingToCloudflare ? 'Deploying…' : 'Publish to Cloudflare' }}
          </button>

          <button
            class="btn-ghost"
            type="button"
            :disabled="previewing"
            title="Get a shareable preview link"
            @click="getPreviewLink"
          >
            <v-icon size="18">mdi-open-in-new</v-icon>
            {{ previewing ? 'Creating link…' : 'Preview link' }}
          </button>
        </div>
      </div>

      <div class="preview-frame-shell">

        <div v-if="builtWithOtherSystem" class="preview-empty-state">
          <div class="preview-empty-icon">
            <v-icon size="30">mdi-creation</v-icon>
          </div>
          <h3>Built with a different system</h3>
          <p>Your live site was made with Site Builder (AI) or Custom Code, not this template builder — this editor can't show or edit it.</p>
          <button type="button" class="preview-empty-cta" @click="router.push('/cms/builder3')">
            <v-icon size="16">mdi-arrow-right</v-icon>
            Open Site Builder (AI)
          </button>
        </div>

        <iframe
           v-else-if="company.company_id"
          class="preview-frame"
          id="previewFrame"
          :src="`/site-preview/${effectiveQuotasticDomain}`"
          title="Site preview"
          @load="syncLivePreview"
        ></iframe>

        <div v-else class="preview-empty-state">
          <div class="preview-empty-icon">
            <v-icon size="30">mdi-domain-off</v-icon>
          </div>
          <h3>No company profile yet</h3>
          <p>Your site preview will appear here once your organization profile is set up.</p>
          <button type="button" class="preview-empty-cta" @click="selectedSetting = '/settings/company'; router.push('/settings')">
            <v-icon size="16">mdi-arrow-right</v-icon>
            Set up company profile
          </button>
        </div>
      </div>
    </main>




    <!-------------------------------------------------------------
    | Right-panel
    -------------------------------------------------------------->    
    <aside class="builder-panel builder-panel-right">
      <div class="panel-header">
        <h2>Settings</h2>
        <p>Content controls</p>
      </div>

      <!-- Template -->
      <div v-if="sectionSelected === 'Template'">

        <div class="template-grid">
          <div
            v-for="template in templates"
            :key="template.id"
            class="template-card"
            :class="{ active: template.id === websiteBuilderState.selectedTemplateId }"
            @click="pickTemplate(template.id)"
          >
            <img
              v-if="template.thumbnail"
              :src="template.thumbnail"
              class="template-card-thumbnail"
              alt=""
            />
            <div v-else class="template-card-thumbnail-empty">
              <v-icon size="28">mdi-web</v-icon>
            </div>
            <div class="template-card-name">
              <span>{{ template.name }}</span>
              <span v-if="template.local" class="template-local-badge">Local</span>
            </div>
          </div>
        </div>
      </div>

      <!-- SEO -->
      <div v-if="sectionSelected === 'SEO'">
        <div class="template-grid">
          <div class="template-card">
            <v-text-field
              v-model="siteTitle"
              label="Site Title"
              placeholder="Browser tab title"
              variant="outlined"
              hide-details="auto"
            />
          </div>
          <div class="template-card">
            <v-textarea
              v-model="metaDescription"
              label="Meta Description"
              placeholder="A short summary shown in search results and link previews"
              variant="outlined"
              rows="3"
              counter="160"
              hide-details="auto"
            />
          </div>
        </div>
      </div>

      <!-- Theme (from template section nav) — shows colors + fonts together -->
      <div v-if="sectionSelected === 'Theme'">
        <div class="template-grid">
          <div
            v-for="(field, key) in sectionFields(builderContent.theme)"
            :key="key"
            class="template-card"
          >
            <template v-if="field.type === 'color'">
              <div class="color-field-row">
                <span class="color-field-label">{{ field.label }}</span>
                <input
                  type="color"
                  :value="content.theme?.[key]?.value ?? field.value"
                  @input="content.theme[key].value = $event.target.value"
                  class="color-picker-input"
                />
                <span class="color-field-hex">{{ content.theme?.[key]?.value ?? field.value }}</span>
              </div>
              <p v-if="field.description" class="theme-field-description">
                {{ field.description }}
              </p>
            </template>
            <template v-else>

              <!-- 
              <v-text-field
                :label="field.label"
                :model-value="content.theme?.[key]?.value ?? field.value"
                variant="outlined"
                hide-details="auto"
                @update:model-value="content.theme[key].value = $event"
              />
              <p v-if="field.description" class="theme-field-description">
                {{ field.description }}
              </p>-->

              <v-select
              :label="field.label"
              :items="FONTS"
              item-title="label"
              item-value="value"
              :model-value="content.theme?.[key]?.value ?? field.value"
              variant="outlined"
              hide-details="auto"
              @update:model-value="content.theme[key].value = $event; updateFontImport()"
              />


            </template>
          </div>
        </div>
      </div>

      <!-- Colors -->
      <div v-if="sectionSelected === 'Colors'">
        <div class="template-grid">
          <div
            v-for="(field, key) in Object.fromEntries(Object.entries(sectionFields(builderContent.theme)).filter(([, f]) => f.type === 'color'))"
            :key="key"
            class="template-card"
          >
            <div class="color-field-row">
              <span class="color-field-label">{{ field.label }}</span>
              <input
                type="color"
                :value="content.theme?.[key]?.value ?? field.value"
                @input="content.theme[key].value = $event.target.value"
                class="color-picker-input"
              />
              <span class="color-field-hex">{{ content.theme?.[key]?.value ?? field.value }}</span>
            </div>
            <p v-if="field.description" class="theme-field-description">
              {{ field.description }}
            </p>
          </div>
        </div>
      </div>

      <!-- Fonts -->
      <div v-if="sectionSelected === 'Fonts'">
        <div class="template-grid">
          <div
            v-for="(field, key) in Object.fromEntries(Object.entries(sectionFields(builderContent.theme)).filter(([, f]) => f.type === 'font'))"
            :key="key"
            class="template-card"
          >

            <v-select
              :label="field.label"
              :items="FONTS"
              item-title="label"
              item-value="value"
              :model-value="content.theme?.[key]?.value ?? field.value"
              variant="outlined"
              hide-details="auto"
              @update:model-value="content.theme[key].value = $event; updateFontImport()"
            />
            <p v-if="field.description" class="theme-field-description">
              {{ field.description }}
            </p>

          </div>





          
        </div>
      </div>

      <!-- Generic content section panel — schema-driven, works for any template -->
      <div v-if="activeSectionKey && !['theme', 'gallery'].includes(activeSectionKey)">
        <div class="template-grid">
          <div
            v-for="(field, key) in sectionFields(builderContent[activeSectionKey])"
            :key="key"
            class="template-card"
          >
            <FieldRenderer
              :section-key="activeSectionKey"
              :field-key="key"
              :field="field"
              :content="content"
              @image-pick="handleImagePickFromField"
            />
          </div>
        </div>
      </div>










      <!-- Gallery — custom UI with reorder controls -->
      <div
        v-if="activeSectionKey === 'gallery'"
        class="gallery-editor"
      >
        <div class="template-grid">
          <div class="template-card">
            <v-text-field
              :label="builderContent.gallery.label.label"
              :model-value="content.gallery?.label?.value ?? builderContent.gallery.label.value"
              variant="outlined"
              @update:model-value="content.gallery.label.value = $event"
            />
          </div>

          <div class="template-card">
            <v-text-field
              :label="builderContent.gallery.title.label"
              :model-value="content.gallery?.title?.value ?? builderContent.gallery.title.value"
              variant="outlined"
              @update:model-value="content.gallery.title.value = $event"
            />
          </div>

          <div
            v-for="(image, index) in (content.gallery?.images?.value ?? builderContent.gallery.images.value)"
            :key="index"
            class="template-card gallery-image-card"
          >
            <div class="gallery-image-card-header">
              <div class="gallery-image-title">
                <v-icon size="17">mdi-image-outline</v-icon>
                <span>Image {{ index + 1 }}</span>
              </div>

              <div class="gallery-image-actions">
                <v-btn
                  icon="mdi-chevron-up"
                  size="x-small"
                  variant="text"
                  aria-label="Move image up"
                  :disabled="index === 0"
                  @click="moveGalleryImage(index, -1)"
                />

                <v-btn
                  icon="mdi-chevron-down"
                  size="x-small"
                  variant="text"
                  aria-label="Move image down"
                  :disabled="index === content.gallery.images.value.length - 1"
                  @click="moveGalleryImage(index, 1)"
                />

                <v-btn
                  icon="mdi-close"
                  size="x-small"
                  variant="text"
                  aria-label="Remove gallery image"
                  @click="removeGalleryImage(index)"
                />
              </div>
            </div>

            <button
              type="button"
              class="gallery-image-preview"
              @click="openGalleryImagePicker(index)"
            >
              <img
                v-if="image.url"
                :src="image.url"
                :alt="image.caption || `Gallery image ${index + 1}`"
              />
              <span
                v-else
                class="gallery-image-placeholder"
              >
                <v-icon size="28">mdi-image-plus-outline</v-icon>
              </span>

              
            </button>
          
            <div class="gallery-url-row">
              <v-text-field
                label="Image URL"
                :model-value="image.url"
                variant="outlined"
                hide-details="auto"
                @update:model-value="content.gallery.images.value[index].url = $event"
                   density="compact"
              />


            </div>

            <v-text-field
               density="compact"
           
              label="Caption"
              :model-value="image.caption"
              variant="outlined"
              hide-details="auto"
              @update:model-value="content.gallery.images.value[index].caption = $event"
            />

            <v-text-field
               density="compact"
              label="Size"
              :model-value="image.size"
              variant="outlined"
              hide-details="auto"
              @update:model-value="content.gallery.images.value[index].size = $event"
            />

          </div>

          <v-btn
            type="button"
            variant="tonal"
            prepend-icon="mdi-plus-circle-outline"
            class="gallery-add-btn"
            @click="addGalleryImage"
          >
            Add image
          </v-btn>


          
        </div>
      </div>





















    </aside>
  </div>
</div>



<ImagePickerModal
  v-model="imagePickerOpen"
  :images="tenantImages"
  :loading="tenantImagesLoading"
  :uploading="tenantImagesUploading"
  :upload-error="tenantImagesUploadError"
  :selected-url="imagePickerSelectedUrl"
  :deletable="true"
  :deleting="tenantImageDeleting"
  title="Choose image"
  subtitle="Select or upload an image."
  @select="handleImageSelect"
  @upload="uploadTenantImage"
  @delete="deleteTenantImage"
/>


</template>









<style scoped>
.mobile-builder-message {
  display: none;
}

@media (max-width: 900px) {
  .desktop-builder {
    display: none;
  }

  .mobile-builder-message {
    display: grid;
  }
}


.builder-page {
  min-height: calc(100dvh - 70px);
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr) 300px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.builder-panel {
  padding: 18px;
  background: var(--surface);
  border-color: var(--border-subtle);
}

.builder-panel-left {
  border-right: 1px solid var(--border-subtle);
}

.builder-panel-right {
  border-left: 1px solid var(--border-subtle);
  height: 100vh;
  overflow-y: scroll;
}

.panel-header {
  margin-bottom: 18px;
}

.panel-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 900;
}

.panel-header p {
  margin: 5px 0 0;
  color: var(--text-secondary);
  font-size: 0.82rem;
}

.tool-button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  margin-bottom: 8px;
  padding: 0 12px;
  background: var(--surface-alt);
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: var(--text-secondary);
  font-weight: 800;
  cursor: pointer;
}

 .tool-button.active {
 
  background:  var(--accent-primary);
  border-color:  var(--accent-primary);
  color: white;
}

.preview-area {
  min-width: 0;
  padding: 18px;
}

.admin-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  padding: 8px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  color: #92400e;
  font-size: 0.84rem;
  font-weight: 700;
}

.admin-banner-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  padding: 4px 10px;
  background: var(--surface);
  border: 1px solid #fde68a;
  border-radius: 5px;
  color: #92400e;
  font: inherit;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}

.preview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 48px;
  margin-bottom: 14px;
}

.preview-toolbar strong,
.preview-toolbar span {
  display: block;
}

.preview-toolbar span {
  margin-top: 3px;
  color: var(--text-secondary);
  font-size: 0.78rem;
}

.preview-toolbar .publish-status {
  max-width: min(52vw, 520px);
  overflow: hidden;
  color: #0f766e;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-toolbar .publish-status-error {
  color: #b91c1c;
}

.preview-toolbar .publish-status a {
  color: inherit;
  font-weight: 800;
  text-decoration: underline;
}

.preview-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Same button system as DomainView.vue/BuilderView3.vue/EmailView.vue
   (var(--accent-primary) = #f26e2e, 8px radius) — this used to be its own
   .preview-toolbar-button style (neutral surface + blue #2169c6 text,
   matching AdminView.vue's admin-action buttons instead of the rest of the
   contractor-facing CMS), the one visibly inconsistent builder toolbar. */
.btn-primary,
.btn-ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
  transition: opacity 0.12s;
}

.btn-primary { border: 0; color: #fff; background: var(--accent-primary, #f26e2e); }
.btn-ghost   { border: 1px solid var(--border-subtle); color: var(--text-secondary); background: var(--surface); }

.btn-primary:disabled,
.btn-ghost:disabled {
  cursor: wait;
  opacity: 0.6;
}

.preview-frame-shell {
  height: calc(100dvh - 150px);
  min-height: 560px;
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.1);
}

.preview-frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  zoom: 80%;
}

.preview-empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 40px;
  text-align: center;
  background: linear-gradient(180deg, var(--surface-alt) 0%, var(--surface-alt) 100%);
}

.preview-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  margin-bottom: 8px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 50%;
  color: var(--accent-primary);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.preview-empty-state h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.05rem;
  font-weight: 900;
}

.preview-empty-state p {
  max-width: 340px;
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.85rem;
}

.preview-empty-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  padding: 0 16px;
  min-height: 40px;
  background: var(--accent-primary);
  border: none;
  border-radius: 6px;
  color: #ffffff;
  font: inherit;
  font-weight: 800;
  font-size: 0.85rem;
  cursor: pointer;
  box-shadow: 0 10px 20px color-mix(in srgb, var(--accent-primary) 30%, transparent);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.preview-empty-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 26px color-mix(in srgb, var(--accent-primary) 38%, transparent);
}

.template-grid {
  display: grid;
  gap: 12px;
}

.template-card {
  padding: 10px;
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  cursor: pointer;
}

.template-card.active {
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--accent-primary) 22%, transparent);
}

.template-card-thumbnail,
.template-card-thumbnail-empty {
  width: 100%;
  aspect-ratio: 16 / 9;
  display: grid;
  place-items: center;
  object-fit: cover;
  margin-bottom: 8px;
  border-radius: 4px;
  background: var(--surface-alt);
  color: var(--text-secondary);
}

.template-card-name {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: var(--text-primary);
  font-size: 0.82rem;
  font-weight: 800;
}

.template-local-badge {
  padding: 2px 6px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--accent-primary) 14%, var(--surface));
  color: var(--accent-primary);
  font-size: 0.64rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.array-field-header {
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  color: var(--text-primary);
  font-size: 0.82rem;
  font-weight: 850;
}

.array-field-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 32px;
  align-items: start;
  gap: 6px;
  margin-bottom: 8px;
}

.gallery-editor .template-grid {
  gap: 14px;
}

.gallery-image-card {
  display: grid;
  gap: 12px;
  padding: 12px;
  cursor: default;
}

.gallery-image-card-header {
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.gallery-image-title {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: var(--text-primary);
  font-size: 0.82rem;
  font-weight: 850;
}

.gallery-image-actions {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

.gallery-image-preview {
  width: 100%;
  aspect-ratio: 16 / 10;
  display: grid;
  place-items: center;
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface-alt);
  cursor: pointer;
}

.gallery-image-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.gallery-image-placeholder {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: var(--text-secondary);
  background:
    linear-gradient(135deg, rgba(33, 105, 198, .08), transparent),
    var(--surface-alt);
}

.gallery-url-row {
  display: grid;
 
  align-items: start;
 
}

.gallery-choose-btn {
  min-width: 42px;
  height: 56px;
}

.gallery-add-btn {
  min-height: 42px;
  justify-self: stretch;
  font-weight: 850;
}

.field-row {
  display: grid;
  gap: 7px;
  margin-bottom: 14px;
}

.field-row span,
.embed-box span {
  color: var(--text-secondary);
  font-size: 0.78rem;
  font-weight: 850;
}

.field-row input {
  width: 100%;
  height: 42px;
  padding: 0 11px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  font: inherit;
}

.embed-box {
  display: grid;
  gap: 8px;
  margin-top: 22px;
}

.embed-box code {
  display: block;
  padding: 12px;
  background: #0f172a;
  color: #dbeafe;
  border-radius: 6px;
  white-space: normal;
  word-break: break-all;
}

@media (max-width: 980px) {
  .builder-page {
    grid-template-columns: 1fr;
  }

  .builder-panel-left,
  .builder-panel-right {
    border: 0;
    border-bottom: 1px solid var(--border-subtle);
  }

  .builder-panel-left {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .builder-panel-left .panel-header {
    grid-column: 1 / -1;
    margin-bottom: 4px;
  }

  .tool-button {
    margin: 0;
  }

  .preview-frame-shell {
    height: 720px;
    min-height: 0;
  }
}


.gallery-image-card :deep(.v-field) {
  min-height: 36px;
}

.gallery-image-card :deep(.v-field__input) {
  min-height: 36px;
}

.color-field-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
}

.color-field-label {
  flex: 1;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-primary);
}

.color-picker-input {
  width: 44px;
  height: 36px;
  padding: 2px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
}

.color-field-hex {
  font-size: 0.8rem;
  color: var(--text-secondary);
  font-family: monospace;
}

.theme-field-description {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 0.78rem;
  line-height: 1.35;
}

.field-style-color-row {
  margin-top: 8px;
}

.field-style-reset-button {
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 8px 10px;
}

.field-style-reset-button:hover {
  border-color: var(--text-secondary);
  color: var(--text-primary);
}

</style>













<!--------------------------------------------------


NOTES 

items to addres******
Bug: max-width: auto; is invalid CSS. Remove it.
Debug styles: Multiple background-color: red!important; should be removed.
Google auth: Google sign-in does not create a Firestore user document like email registration does. Could cause inconsistencies.
Security: alert(err.message) exposes raw Firebase errors to users.
UX: Email/password fields are cleared, but register fields are not.
Performance: Many inline styles should be moved to CSS classes.
Consistency: role: "user" should ideally come from backend rules, not client code.


****Schema
 

templates
---------
id
name
thumbnail_url
vue_code
css_code
version
created_at

or even:

templates
---------
id
name
template_json

where template_json contains:

{
  "vue": "<template>...</template>",
  "script": "<script setup>...</script>",
  "css": "<style>...</style>"
}

-->
